/**
 * AdaptiveRow — a row of equal columns that becomes a stack when the user
 * turns their text size up.
 *
 * Side-by-side columns are the one layout that cannot be rescued by growing:
 * a half-width form field and a one-third stat card each get a fixed slice of
 * a fixed viewport, and past ~1.5× the OS text scale a single Spanish word
 * ("Vehículos", "Ej: Carlos") is wider than its slice. What the platform does
 * then is break *inside the word* — "Vehí / culos / activos" — or clip it.
 * Neither is a legible answer, and shrinking the text back to fit is the
 * setting ignored.
 *
 * So the columns stop being columns. Same children, same order, one per line,
 * each at full width, text at exactly the size the user asked for.
 *
 * Children are wrapped here rather than at the call site on purpose: in a row
 * each needs `flex: 1` to share the width, and in a stack that same `flex: 1`
 * would make each child share the *height* instead — the bug this component
 * exists to avoid having to remember.
 *
 * At scale 1.0 this renders exactly what a plain
 * `<View style={{ flexDirection: 'row', gap }}>` with `flex: 1` children
 * renders, so it is safe to drop into an approved screen.
 */
import { Children, type ReactNode } from 'react'
import { View, type ViewStyle } from 'react-native'
import { useFontScale } from '../../lib/hooks/use-font-scale'
import { SPACING } from '../../lib/theme/tokens'

interface AdaptiveRowProps {
  children: ReactNode
  /** Gap between columns (row) or between rows (stacked). */
  gap?: number
  /**
   * Text multiplier at which the row stacks. Defaults to the app-wide
   * `STACK_THRESHOLD` (1.5) — raise it for rows whose columns hold short
   * content that survives longer.
   */
  stackAt?: number
  style?: ViewStyle
  testID?: string
}

export function AdaptiveRow({
  children,
  gap = SPACING.md,
  stackAt,
  style,
  testID,
}: AdaptiveRowProps) {
  const { scale, shouldStack } = useFontScale()
  const stacked = stackAt == null ? shouldStack : scale >= stackAt

  return (
    <View
      testID={testID}
      style={{
        flexDirection: stacked ? 'column' : 'row',
        gap,
        ...style,
      }}
    >
      {Children.map(children, (child, i) =>
        child == null ? null : (
          // `minWidth: 0` lets a row column shrink below its content's
          // intrinsic width instead of pushing its siblings off the edge.
          <View key={i} style={stacked ? undefined : { flex: 1, minWidth: 0 }}>
            {child}
          </View>
        ),
      )}
    </View>
  )
}
